import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

interface SeasonRecord {
  season_id: number;
  season_year: string;
  season_start: string; // YYYY-MM-DD
  season_end: string;   // YYYY-MM-DD
  auto_handicap_calculation?: boolean | null; // null = inherit league default
}

interface RegularRow { num_players: number; rank: number; points: number; }
interface MajorRow { num_rounds: number; num_players: number; rank: number; points: number; }
interface TeamConfig { regular_team_points_18: number; regular_team_points_9: number; tournament_team_points_per_round: number; min_players_per_team: number; team_competition_enabled: boolean; }
type Source = 'custom' | 'default';
type EditorTarget = { type: 'season'; season: SeasonRecord } | { type: 'default' };

const C = {
  blue: '#2563eb', blueDark: '#1e40af', slate: '#1e293b', muted: '#64748b',
  line: '#e2e8f0', bg: '#f8fafc', soft: '#f1f5f9', red: '#dc2626',
};

const s = {
  page: { background: C.bg, minHeight: '100vh', padding: '24px 16px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" } as React.CSSProperties,
  shell: { maxWidth: 920, margin: '0 auto' } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 800, color: C.slate, margin: 0 } as React.CSSProperties,
  subtitle: { color: C.muted, fontSize: 14, margin: '4px 0 22px' } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, border: `1px solid ${C.line}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 18, overflow: 'hidden' } as React.CSSProperties,
  cardHead: { padding: '14px 18px', borderBottom: `1px solid ${C.soft}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' } as React.CSSProperties,
  cardTitle: { fontSize: 16, fontWeight: 700, color: C.slate, margin: 0, display: 'flex', alignItems: 'center', gap: 10 } as React.CSSProperties,
  body: { padding: 18 } as React.CSSProperties,
  fieldRow: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' } as React.CSSProperties,
  field: { display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 160px', minWidth: 0 } as React.CSSProperties,
  label: { fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.03em' } as React.CSSProperties,
  input: { padding: '9px 11px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 14, color: C.slate, background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box' } as React.CSSProperties,
  seasonRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', padding: '14px 18px', borderBottom: `1px solid ${C.soft}` } as React.CSSProperties,
  seasonMeta: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 } as React.CSSProperties,
  seasonYear: { fontSize: 17, fontWeight: 800, color: C.slate } as React.CSSProperties,
  seasonDates: { fontSize: 13, color: C.muted } as React.CSSProperties,
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' } as React.CSSProperties,
  btn: { padding: '8px 14px', borderRadius: 8, border: 'none', background: C.blue, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 } as React.CSSProperties,
  btnGhost: { padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff', color: C.slate, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  btnDanger: { padding: '8px 12px', borderRadius: 8, border: `1px solid #fecaca`, background: '#fff', color: C.red, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  err: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14, cursor: 'pointer' } as React.CSSProperties,
  empty: { padding: '28px 18px', textAlign: 'center', color: '#94a3b8', fontSize: 14 } as React.CSSProperties,
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 12px', overflowY: 'auto', zIndex: 1000 } as React.CSSProperties,
  modal: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' } as React.CSSProperties,
  modalHead: { padding: '18px 20px', borderBottom: `1px solid ${C.soft}` } as React.CSSProperties,
  modalBody: { padding: 20, maxHeight: '60vh', overflowY: 'auto' } as React.CSSProperties,
  modalFoot: { padding: '14px 20px', borderTop: `1px solid ${C.soft}`, display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' } as React.CSSProperties,
  tabs: { display: 'flex', gap: 6, background: C.soft, padding: 4, borderRadius: 10, marginBottom: 14 } as React.CSSProperties,
  filterBar: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 } as React.CSSProperties,
  sel: { padding: '7px 10px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13, background: '#fff', color: C.slate } as React.CSSProperties,
  addBar: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8, padding: '12px', background: C.soft, borderRadius: 10 } as React.CSSProperties,
  mini: { width: 90, padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13 } as React.CSSProperties,
  group: { border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px', marginBottom: 10 } as React.CSSProperties,
  groupHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } as React.CSSProperties,
  groupLabel: { fontWeight: 700, fontSize: 13, color: '#334155' } as React.CSSProperties,
  ranks: { display: 'flex', flexWrap: 'wrap', gap: 10 } as React.CSSProperties,
  rankCell: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, fontSize: 11, color: C.muted, fontWeight: 700 } as React.CSSProperties,
  num: { width: 62, padding: '6px 8px', border: `1px solid #cbd5e1`, borderRadius: 8, textAlign: 'right', fontSize: 14 } as React.CSSProperties,
  remove: { border: 'none', background: 'transparent', color: C.red, cursor: 'pointer', fontWeight: 700, fontSize: 12 } as React.CSSProperties,
  badge: (src: Source): React.CSSProperties => ({ display: 'inline-block', fontSize: 11, fontWeight: 800, padding: '2px 9px', borderRadius: 999, letterSpacing: '0.02em', textTransform: 'uppercase', background: src === 'custom' ? '#dcfce7' : C.soft, color: src === 'custom' ? '#166534' : C.muted }),
  tabBtn: (active: boolean): React.CSSProperties => ({ flex: 1, padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, background: active ? '#fff' : 'transparent', color: active ? C.blueDark : C.muted, boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }),
};

const Seasons: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const [seasons, setSeasons] = useState<SeasonRecord[]>([]);
  const [newSeason, setNewSeason] = useState({ season_year: '', season_start: '', season_end: '' });
  const [editingSeason, setEditingSeason] = useState<SeasonRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Points editor
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [pointsTab, setPointsTab] = useState<'regular' | 'major' | 'team'>('regular');
  const [pdRegular, setPdRegular] = useState<RegularRow[]>([]);
  const [pdMajor, setPdMajor] = useState<MajorRow[]>([]);
  const [pdSource, setPdSource] = useState<{ regular: Source; major: Source }>({ regular: 'default', major: 'default' });
  const [pdTeam, setPdTeam] = useState<TeamConfig>({ regular_team_points_18: 2, regular_team_points_9: 1, tournament_team_points_per_round: 2, min_players_per_team: 3, team_competition_enabled: true });
  const [pdTeamSource, setPdTeamSource] = useState<Source>('default');
  const [pdLoading, setPdLoading] = useState(false);
  const [pdSaving, setPdSaving] = useState(false);
  // filters + add inputs
  const [regFilterPlayers, setRegFilterPlayers] = useState(0); // 0 = all
  const [majFilterRounds, setMajFilterRounds] = useState(0);
  const [majFilterPlayers, setMajFilterPlayers] = useState(0);
  const [addReg, setAddReg] = useState('');
  const [addMajR, setAddMajR] = useState('');
  const [addMajP, setAddMajP] = useState('');

  // Clone
  const [cloneFrom, setCloneFrom] = useState<SeasonRecord | null>(null);
  const [cloneForm, setCloneForm] = useState({ season_year: '', season_start: '', season_end: '' });

  const APIURL = process.env.REACT_APP_API_URL;
  const authHeader = useCallback(async () => ({ Authorization: `Bearer ${await getAccessTokenSilently()}` }), [getAccessTokenSilently]);

  const fetchSeasons = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await authHeader();
      const { data } = await axios.get<SeasonRecord[]>(`${APIURL}/seasons`, { headers });
      setSeasons([...data].sort((a, b) => b.season_year.localeCompare(a.season_year)));
    } catch {
      setError('Failed to fetch seasons.');
    } finally {
      setLoading(false);
    }
  }, [APIURL, authHeader]);

  useEffect(() => {
    if (isAuthenticated) fetchSeasons();
    else if (!isLoading) loginWithRedirect();
  }, [isAuthenticated, isLoading, loginWithRedirect, fetchSeasons]);

  const handleAddSeason = async () => {
    try {
      const headers = await authHeader();
      const { data } = await axios.post(`${APIURL}/seasons`, newSeason, { headers });
      setSeasons((prev) => [data, ...prev].sort((a, b) => b.season_year.localeCompare(a.season_year)));
      setNewSeason({ season_year: '', season_start: '', season_end: '' });
      setError(null);
    } catch {
      setError('Failed to add season. Check the year and that start is before end.');
    }
  };

  const handleEditSeason = async () => {
    if (!editingSeason) return;
    try {
      const headers = await authHeader();
      const { data } = await axios.put(`${APIURL}/seasons/${editingSeason.season_id}`, editingSeason, { headers });
      setSeasons((prev) => prev.map((x) => (x.season_id === editingSeason.season_id ? data : x)));
      setEditingSeason(null);
    } catch {
      setError('Failed to edit season.');
    }
  };

  const handleDeleteSeason = async (id: number) => {
    if (!window.confirm('Delete this season? Its custom points breakdown will also be removed.')) return;
    try {
      const headers = await authHeader();
      await axios.delete(`${APIURL}/seasons/${id}`, { headers });
      setSeasons((prev) => prev.filter((x) => x.season_id !== id));
    } catch {
      setError('Failed to delete season.');
    }
  };

  // Per-season automatic-handicap override. value: true | false | null (inherit).
  const setSeasonHandicap = async (season: SeasonRecord, value: boolean | null) => {
    try {
      const headers = await authHeader();
      await axios.patch(`${APIURL}/seasons/${season.season_id}/auto-handicap`, { value }, { headers });
      setSeasons((prev) => prev.map((x) => (x.season_id === season.season_id ? { ...x, auto_handicap_calculation: value } : x)));
    } catch {
      setError('Failed to update the season handicap setting.');
    }
  };

  // ── Points breakdown (season or global default) ──
  const editorUrl = (t: EditorTarget) =>
    t.type === 'season' ? `${APIURL}/seasons/${t.season.season_id}/points-distribution` : `${APIURL}/points-distribution/default`;

  const openEditor = async (target: EditorTarget) => {
    setEditorTarget(target);
    setPointsTab('regular');
    setRegFilterPlayers(0); setMajFilterRounds(0); setMajFilterPlayers(0);
    setAddReg(''); setAddMajR(''); setAddMajP('');
    setPdLoading(true);
    try {
      const headers = await authHeader();
      const { data } = await axios.get(editorUrl(target), { headers });
      setPdRegular(data.regular.rows);
      setPdMajor(data.major.rows);
      setPdSource({ regular: data.regular.source, major: data.major.source });
      setPdTeam({
        regular_team_points_18: data.team?.regular_team_points_18 ?? 2,
        regular_team_points_9: data.team?.regular_team_points_9 ?? 1,
        tournament_team_points_per_round: data.team?.tournament_team_points_per_round ?? 2,
        min_players_per_team: data.team?.min_players_per_team ?? 3,
        team_competition_enabled: data.team?.team_competition_enabled ?? true,
      });
      setPdTeamSource(data.team?.source ?? 'default');
    } catch {
      setError('Failed to load points breakdown.');
      setEditorTarget(null);
    } finally {
      setPdLoading(false);
    }
  };

  const setRegularPoints = (np: number, rank: number, points: number) =>
    setPdRegular((rows) => rows.map((r) => (r.num_players === np && r.rank === rank ? { ...r, points } : r)));
  const setMajorPoints = (nr: number, np: number, rank: number, points: number) =>
    setPdMajor((rows) => rows.map((r) => (r.num_rounds === nr && r.num_players === np && r.rank === rank ? { ...r, points } : r)));

  const addRegularGroup = () => {
    const n = parseInt(addReg, 10);
    if (!n || n < 1 || n > 100) return;
    if (!pdRegular.some((r) => r.num_players === n)) {
      setPdRegular((prev) => [...prev, ...Array.from({ length: n }, (_, i) => ({ num_players: n, rank: i + 1, points: 0 }))]);
    }
    setRegFilterPlayers(n); setAddReg('');
  };
  const removeRegularGroup = (n: number) => setPdRegular((prev) => prev.filter((r) => r.num_players !== n));

  const addMajorGroup = () => {
    const nr = parseInt(addMajR, 10), np = parseInt(addMajP, 10);
    if (!nr || !np || nr < 1 || np < 1 || nr > 100 || np > 100) return;
    if (!pdMajor.some((r) => r.num_rounds === nr && r.num_players === np)) {
      setPdMajor((prev) => [...prev, ...Array.from({ length: np }, (_, i) => ({ num_rounds: nr, num_players: np, rank: i + 1, points: 0 }))]);
    }
    setMajFilterRounds(nr); setMajFilterPlayers(np); setAddMajR(''); setAddMajP('');
  };
  const removeMajorGroup = (nr: number, np: number) => setPdMajor((prev) => prev.filter((r) => !(r.num_rounds === nr && r.num_players === np)));

  const savePoints = async () => {
    if (!editorTarget) return;
    setPdSaving(true);
    try {
      const headers = await authHeader();
      await axios.put(editorUrl(editorTarget), { regular: pdRegular, major: pdMajor, team: pdTeam }, { headers });
      setEditorTarget(null);
    } catch {
      setError('Failed to save points breakdown.');
    } finally {
      setPdSaving(false);
    }
  };

  const resetPoints = async () => {
    if (!editorTarget || editorTarget.type !== 'season') return;
    if (!window.confirm('Reset this season to the default points breakdown?')) return;
    try {
      const headers = await authHeader();
      await axios.delete(`${APIURL}/seasons/${editorTarget.season.season_id}/points-distribution`, { headers });
      await openEditor(editorTarget);
    } catch {
      setError('Failed to reset points breakdown.');
    }
  };

  const submitClone = async () => {
    if (!cloneFrom) return;
    try {
      const headers = await authHeader();
      const { data } = await axios.post(`${APIURL}/seasons/${cloneFrom.season_id}/clone`, cloneForm, { headers });
      setSeasons((prev) => [data, ...prev].sort((a, b) => b.season_year.localeCompare(a.season_year)));
      setCloneFrom(null);
    } catch {
      setError('Failed to clone season. Check the year and dates.');
    }
  };

  const clampInt = (v: string) => Math.max(0, parseInt(v || '0', 10) || 0);
  const isDefault = editorTarget?.type === 'default';

  // derived view data
  const regularPlayerCounts = Array.from(new Set(pdRegular.map((r) => r.num_players))).sort((a, b) => a - b);
  const shownRegular = regularPlayerCounts.filter((np) => !regFilterPlayers || np === regFilterPlayers);
  const majorGroups = Array.from(new Set(pdMajor.map((r) => `${r.num_rounds}|${r.num_players}`)))
    .map((k) => { const [nr, np] = k.split('|').map(Number); return { nr, np }; })
    .sort((a, b) => a.nr - b.nr || a.np - b.np);
  const majorRoundOpts = Array.from(new Set(pdMajor.map((r) => r.num_rounds))).sort((a, b) => a - b);
  const majorPlayerOpts = Array.from(new Set(pdMajor.map((r) => r.num_players))).sort((a, b) => a - b);
  const shownMajor = majorGroups.filter((g) => (!majFilterRounds || g.nr === majFilterRounds) && (!majFilterPlayers || g.np === majFilterPlayers));

  if (isLoading || loading) return <div style={s.page}><div style={s.shell}><p style={{ color: C.muted }}>Loading…</p></div></div>;

  const addReady = newSeason.season_year && newSeason.season_start && newSeason.season_end;

  return (
    <div style={s.page}>
      <div style={s.shell}>
        <h1 style={s.title}>Seasons</h1>
        <p style={s.subtitle}>Manage seasons and the points awarded for rounds played within each season’s dates.</p>

        {error && <div style={s.err} onClick={() => setError(null)}>{error}</div>}

        {/* Default points */}
        <div style={s.card}>
          <div style={s.cardHead}>
            <h2 style={s.cardTitle}><span style={{ fontSize: 18 }}>⚙️</span> Default points</h2>
            <button style={s.btnGhost} onClick={() => openEditor({ type: 'default' })}>Edit default breakdown</button>
          </div>
          <div style={{ ...s.body, color: C.muted, fontSize: 13, paddingTop: 12 }}>
            The fallback used by any season that hasn’t set its own breakdown. Set everything to 0 if you want only
            explicitly-configured seasons to award points (then use <strong>Clone</strong> to seed each season that should).
          </div>
        </div>

        {/* Add season */}
        <div style={s.card}>
          <div style={s.cardHead}><h2 style={s.cardTitle}><span style={{ fontSize: 18 }}>➕</span> Add a season</h2></div>
          <div style={s.body}>
            <div style={s.fieldRow}>
              <div style={s.field}><span style={s.label}>Year / name</span>
                <input style={s.input} type="text" placeholder="e.g. 2027" value={newSeason.season_year}
                  onChange={(e) => setNewSeason({ ...newSeason, season_year: e.target.value })} /></div>
              <div style={s.field}><span style={s.label}>Start date</span>
                <input style={s.input} type="date" value={newSeason.season_start}
                  onChange={(e) => setNewSeason({ ...newSeason, season_start: e.target.value })} /></div>
              <div style={s.field}><span style={s.label}>End date</span>
                <input style={s.input} type="date" value={newSeason.season_end}
                  onChange={(e) => setNewSeason({ ...newSeason, season_end: e.target.value })} /></div>
              <button style={{ ...s.btn, opacity: addReady ? 1 : 0.5, flex: '0 0 auto' }} onClick={handleAddSeason} disabled={!addReady}>Add season</button>
            </div>
          </div>
        </div>

        {/* Season list */}
        <div style={s.card}>
          <div style={s.cardHead}><h2 style={s.cardTitle}><span style={{ fontSize: 18 }}>📅</span> All seasons</h2></div>
          {seasons.length === 0 ? (
            <div style={s.empty}>No seasons yet. Add your first one above.</div>
          ) : seasons.map((season) => {
            const isEditing = editingSeason?.season_id === season.season_id;
            return (
              <div key={season.season_id} style={s.seasonRow}>
                {isEditing ? (
                  <div style={{ ...s.fieldRow, flex: '1 1 100%' }}>
                    <div style={s.field}><span style={s.label}>Year</span>
                      <input style={s.input} type="text" value={editingSeason.season_year}
                        onChange={(e) => setEditingSeason({ ...editingSeason, season_year: e.target.value })} /></div>
                    <div style={s.field}><span style={s.label}>Start</span>
                      <input style={s.input} type="date" value={editingSeason.season_start}
                        onChange={(e) => setEditingSeason({ ...editingSeason, season_start: e.target.value })} /></div>
                    <div style={s.field}><span style={s.label}>End</span>
                      <input style={s.input} type="date" value={editingSeason.season_end}
                        onChange={(e) => setEditingSeason({ ...editingSeason, season_end: e.target.value })} /></div>
                    <div style={s.actions}>
                      <button style={s.btn} onClick={handleEditSeason}>Save</button>
                      <button style={s.btnGhost} onClick={() => setEditingSeason(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={s.seasonMeta}>
                      <span style={s.seasonYear}>{season.season_year}</span>
                      <span style={s.seasonDates}>{season.season_start} → {season.season_end}</span>
                    </div>
                    <div style={s.actions}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}
                        title="Automatic handicap calculation for this season. 'Default' inherits the league setting.">
                        <span>Auto handicap</span>
                        <select
                          style={{ ...s.input, padding: '4px 6px', width: 'auto', minWidth: 96 }}
                          value={season.auto_handicap_calculation == null ? 'default' : season.auto_handicap_calculation ? 'on' : 'off'}
                          onChange={(e) => setSeasonHandicap(season, e.target.value === 'default' ? null : e.target.value === 'on')}
                        >
                          <option value="default">Default</option>
                          <option value="on">On</option>
                          <option value="off">Off</option>
                        </select>
                      </label>
                      <button style={s.btn} onClick={() => openEditor({ type: 'season', season })}>🏆 Points</button>
                      <button style={s.btnGhost} onClick={() => { setCloneFrom(season); setCloneForm({ season_year: '', season_start: '', season_end: '' }); }}>⧉ Clone</button>
                      <button style={s.btnGhost} onClick={() => setEditingSeason(season)}>✎ Edit</button>
                      <button style={s.btnDanger} onClick={() => handleDeleteSeason(season.season_id)}>🗑 Delete</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Points editor modal */}
      {editorTarget && (
        <div style={s.overlay} onClick={() => setEditorTarget(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHead}>
              <h2 style={{ ...s.cardTitle, fontSize: 18 }}>
                {isDefault ? '⚙️ Default points breakdown' : `🏆 Points breakdown · ${editorTarget.type === 'season' ? editorTarget.season.season_year : ''}`}
              </h2>
              <p style={{ color: C.muted, fontSize: 13, margin: '6px 0 0' }}>
                {isDefault
                  ? 'Applies to every season that hasn’t set its own breakdown.'
                  : <>Rounds dated <strong>{editorTarget.type === 'season' ? editorTarget.season.season_start : ''} → {editorTarget.type === 'season' ? editorTarget.season.season_end : ''}</strong> earn these points. Changes apply to this season only.</>}
              </p>
            </div>

            {pdLoading ? <div style={s.empty}>Loading breakdown…</div> : (
              <>
                <div style={s.modalBody}>
                  <div style={s.tabs}>
                    <button style={s.tabBtn(pointsTab === 'regular')} onClick={() => setPointsTab('regular')}>
                      Regular season {!isDefault && <span style={s.badge(pdSource.regular)}>{pdSource.regular}</span>}
                    </button>
                    <button style={s.tabBtn(pointsTab === 'major')} onClick={() => setPointsTab('major')}>
                      Majors {!isDefault && <span style={s.badge(pdSource.major)}>{pdSource.major}</span>}
                    </button>
                    <button style={s.tabBtn(pointsTab === 'team')} onClick={() => setPointsTab('team')}>
                      Team points {!isDefault && <span style={s.badge(pdTeamSource)}>{pdTeamSource}</span>}
                    </button>
                  </div>

                  {pointsTab === 'regular' ? (
                    <>
                      <div style={s.filterBar}>
                        <div style={s.field}><span style={s.label}>Filter by players</span>
                          <select style={s.sel} value={String(regFilterPlayers)} onChange={(e) => setRegFilterPlayers(Number(e.target.value))}>
                            <option value="0">All field sizes</option>
                            {regularPlayerCounts.map((n) => <option key={n} value={n}>{n} players</option>)}
                          </select>
                        </div>
                      </div>
                      {shownRegular.length === 0 ? <div style={s.empty}>No field sizes yet — add one below.</div> :
                        shownRegular.map((np) => (
                          <div key={np} style={s.group}>
                            <div style={s.groupHead}>
                              <span style={s.groupLabel}>{np} players</span>
                              <button style={s.remove} onClick={() => removeRegularGroup(np)}>✕ remove</button>
                            </div>
                            <div style={s.ranks}>
                              {pdRegular.filter((r) => r.num_players === np).sort((a, b) => a.rank - b.rank).map((r) => (
                                <label key={r.rank} style={s.rankCell}>#{r.rank}
                                  <input type="number" min={0} style={s.num} value={r.points}
                                    onChange={(e) => setRegularPoints(np, r.rank, clampInt(e.target.value))} /></label>
                              ))}
                            </div>
                          </div>
                        ))}
                      <div style={s.addBar}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={s.label}>Add field size (players)</span>
                          <input style={s.mini} type="number" min={1} placeholder="e.g. 8" value={addReg} onChange={(e) => setAddReg(e.target.value)} />
                        </div>
                        <button style={s.btn} onClick={addRegularGroup}>Add</button>
                      </div>
                    </>
                  ) : pointsTab === 'major' ? (
                    <>
                      <div style={s.filterBar}>
                        <div style={s.field}><span style={s.label}>Filter by rounds</span>
                          <select style={s.sel} value={String(majFilterRounds)} onChange={(e) => setMajFilterRounds(Number(e.target.value))}>
                            <option value="0">All rounds</option>
                            {majorRoundOpts.map((n) => <option key={n} value={n}>{n} rounds</option>)}
                          </select>
                        </div>
                        <div style={s.field}><span style={s.label}>Filter by players</span>
                          <select style={s.sel} value={String(majFilterPlayers)} onChange={(e) => setMajFilterPlayers(Number(e.target.value))}>
                            <option value="0">All field sizes</option>
                            {majorPlayerOpts.map((n) => <option key={n} value={n}>{n} players</option>)}
                          </select>
                        </div>
                      </div>
                      {shownMajor.length === 0 ? <div style={s.empty}>No groups match — adjust filters or add one below.</div> :
                        shownMajor.map(({ nr, np }) => (
                          <div key={`${nr}|${np}`} style={s.group}>
                            <div style={s.groupHead}>
                              <span style={s.groupLabel}>{nr} rounds · {np} players</span>
                              <button style={s.remove} onClick={() => removeMajorGroup(nr, np)}>✕ remove</button>
                            </div>
                            <div style={s.ranks}>
                              {pdMajor.filter((r) => r.num_rounds === nr && r.num_players === np).sort((a, b) => a.rank - b.rank).map((r) => (
                                <label key={r.rank} style={s.rankCell}>#{r.rank}
                                  <input type="number" min={0} style={s.num} value={r.points}
                                    onChange={(e) => setMajorPoints(nr, np, r.rank, clampInt(e.target.value))} /></label>
                              ))}
                            </div>
                          </div>
                        ))}
                      <div style={s.addBar}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={s.label}>Rounds</span>
                          <input style={s.mini} type="number" min={1} placeholder="e.g. 3" value={addMajR} onChange={(e) => setAddMajR(e.target.value)} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={s.label}>Players</span>
                          <input style={s.mini} type="number" min={1} placeholder="e.g. 6" value={addMajP} onChange={(e) => setAddMajP(e.target.value)} />
                        </div>
                        <button style={s.btn} onClick={addMajorGroup}>Add group</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p style={{ color: C.muted, fontSize: 13, margin: '0 0 14px' }}>
                        Points awarded for winning the team competition. Regular rounds pay each
                        winning-team player per round; majors pay per round played (total = rounds × value) — unless a
                        specific major overrides it by tagging players on the Majors page. Rosters are managed on the
                        <strong> Teams</strong> page.
                      </p>
                      <div style={s.group}>
                        <div style={s.groupHead}><span style={s.groupLabel}>Rules</span></div>
                        <div style={s.ranks}>
                          <label style={s.rankCell}>Min players / team
                            <input type="number" min={1} style={s.num} value={pdTeam.min_players_per_team}
                              onChange={(e) => setPdTeam((t) => ({ ...t, min_players_per_team: Math.max(1, clampInt(e.target.value)) }))} /></label>
                          <label style={{ ...s.rankCell, flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                            <input type="checkbox" checked={pdTeam.team_competition_enabled}
                              onChange={(e) => setPdTeam((t) => ({ ...t, team_competition_enabled: e.target.checked }))} />
                            Team competition enabled
                          </label>
                        </div>
                      </div>
                      <div style={s.group}>
                        <div style={s.groupHead}><span style={s.groupLabel}>Regular season rounds</span></div>
                        <div style={s.ranks}>
                          <label style={s.rankCell}>18-hole win
                            <input type="number" min={0} style={s.num} value={pdTeam.regular_team_points_18}
                              onChange={(e) => setPdTeam((t) => ({ ...t, regular_team_points_18: clampInt(e.target.value) }))} /></label>
                          <label style={s.rankCell}>9-hole win
                            <input type="number" min={0} style={s.num} value={pdTeam.regular_team_points_9}
                              onChange={(e) => setPdTeam((t) => ({ ...t, regular_team_points_9: clampInt(e.target.value) }))} /></label>
                        </div>
                      </div>
                      <div style={s.group}>
                        <div style={s.groupHead}><span style={s.groupLabel}>Majors (per round played)</span></div>
                        <div style={s.ranks}>
                          <label style={s.rankCell}>Points / round
                            <input type="number" min={0} style={s.num} value={pdTeam.tournament_team_points_per_round}
                              onChange={(e) => setPdTeam((t) => ({ ...t, tournament_team_points_per_round: clampInt(e.target.value) }))} /></label>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div style={s.modalFoot}>
                  {!isDefault && <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={resetPoints}>Reset to default</button>}
                  <button style={s.btnGhost} onClick={() => setEditorTarget(null)}>Cancel</button>
                  <button style={{ ...s.btn, opacity: pdSaving ? 0.6 : 1 }} onClick={savePoints} disabled={pdSaving}>
                    {pdSaving ? 'Saving…' : (isDefault ? 'Save default' : 'Save all')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Clone modal */}
      {cloneFrom && (
        <div style={s.overlay} onClick={() => setCloneFrom(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHead}>
              <h2 style={{ ...s.cardTitle, fontSize: 18 }}>Clone “{cloneFrom.season_year}”</h2>
              <p style={{ color: C.muted, fontSize: 13, margin: '6px 0 0' }}>Creates a new season and copies this season’s points breakdown into it.</p>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={s.field}><span style={s.label}>New year / name</span>
                  <input style={s.input} type="text" placeholder="e.g. 2027" value={cloneForm.season_year}
                    onChange={(e) => setCloneForm({ ...cloneForm, season_year: e.target.value })} /></div>
                <div style={s.field}><span style={s.label}>Start date</span>
                  <input style={s.input} type="date" value={cloneForm.season_start}
                    onChange={(e) => setCloneForm({ ...cloneForm, season_start: e.target.value })} /></div>
                <div style={s.field}><span style={s.label}>End date</span>
                  <input style={s.input} type="date" value={cloneForm.season_end}
                    onChange={(e) => setCloneForm({ ...cloneForm, season_end: e.target.value })} /></div>
              </div>
            </div>
            <div style={s.modalFoot}>
              <button style={s.btnGhost} onClick={() => setCloneFrom(null)}>Cancel</button>
              <button style={{ ...s.btn, opacity: (cloneForm.season_year && cloneForm.season_start && cloneForm.season_end) ? 1 : 0.5 }}
                onClick={submitClone} disabled={!cloneForm.season_year || !cloneForm.season_start || !cloneForm.season_end}>
                Clone season
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Seasons;
